import React, { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  TextField,
  Button,
  Chip,
  Typography,
  IconButton,
  Tooltip,
  Alert,
  Stack,
  Paper,
  Divider,
  useTheme,
  alpha
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  FilterAlt as FilterIcon,
  CalendarToday as CalendarIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';
import SucursalAutocomplete from './SucursalAutocomplete';
import { useFechas } from '../../pedidosEnvasado/context/FechaContext';
import { getData } from "../../../../services/library";

const FiltrosResumen = ({ onFilter, onClear }) => {
  const theme = useTheme();
  const { desde, setDesde, hasta, setHasta } = useFechas();
  const [sucursal, setSucursal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [sucursales, setSucursales] = useState([]);

  // Obtener lista de sucursales para mostrar el nombre en los chips
  useEffect(() => {
    getData("/sucursales/xsucauth/" + localStorage.getItem("suc_auth"), setSucursales);
  }, []);

  const formatDateDisplay = (dateString) => {
    if (!dateString) return '';
    const parts = dateString.split('-');
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  };

  // Obtener nombre de sucursal por ID
  const getNombreSucursal = (id) => {
    const found = sucursales.find(s => s.id === id);
    return found ? found.nombre : id;
  };

  const handleFilter = async () => {
    if (!sucursal || !desde || !hasta) {
      setError('Por favor, completa todos los filtros antes de buscar.');
      return;
    }

    setError(null);
    setLoading(true);
    try {
      
      const filtros = {
        sucursal,
        desde,
        hasta
      };
      await onFilter(filtros);
    } catch (error) {
      setError('Error al aplicar los filtros. Intenta nuevamente.');
      console.error('Error al filtrar:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleClearFilters = () => {
    setSucursal(null);
    setError(null);
    
    if (onClear) {
      onClear();
    }
  };

  const handleDesdeChange = (e) => {
    const value = e.target.value;
    setDesde(value);
    if (hasta && value > hasta) {
      setError('La fecha "Desde" no puede ser mayor que la fecha "Hasta"');
    } else {
      setError(null);
    }
  };

  const handleHastaChange = (e) => {
    const value = e.target.value;
    setHasta(value);
    if (desde && value < desde) {
      setError('La fecha "Hasta" no puede ser menor que la fecha "Desde"');
    } else {
      setError(null);
    }
  };

  const handleSucursalChange = (newSucursal) => {
    setSucursal(newSucursal);
    setError(null);
  };

  const isFormValid = sucursal && desde && hasta;

  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 0,
        borderBottom: `1px solid ${theme.palette.divider}`,
        backgroundColor: '#f8f9fa',
        position: 'sticky',
        top: 0,
        zIndex: theme.zIndex.appBar,
      }}
    >
      <Box sx={{ px: 3, py: 1.5 }}>
        {/* Título y filtros activos */}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <FilterIcon sx={{ mr: 1, fontSize: 22, color: theme.palette.primary.main }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary' }}>
              Filtros
            </Typography>
          </Box>
          
          {/* Filtros activos con nombre de sucursal */}
          {(sucursal || desde || hasta) && (
            <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
              <Typography variant="caption" color="textSecondary" sx={{ mr: 0.5 }}>
                Activos:
              </Typography>
              {sucursal && (
                <Chip
                  label={`Sucursal: ${getNombreSucursal(sucursal.id)}`}
                  size="small"
                  onDelete={() => setSucursal(null)}
                  icon={<BusinessIcon sx={{ fontSize: 16 }} />}
                  color="primary"
                  variant="filled"
                  sx={{ 
                    height: 26,
                    '& .MuiChip-label': { fontSize: '0.7rem', px: 0.75 },
                    '& .MuiChip-icon': { fontSize: 16, ml: 0.5 }
                  }}
                />
              )}
              {desde && (
                <Chip
                  label={`Desde: ${formatDateDisplay(desde)}`}
                  size="small"
                  onDelete={() => setDesde('')}
                  icon={<CalendarIcon sx={{ fontSize: 16 }} />}
                  color="primary"
                  variant="outlined"
                  sx={{ 
                    height: 26,
                    '& .MuiChip-label': { fontSize: '0.7rem', px: 0.75 },
                    '& .MuiChip-icon': { fontSize: 16, ml: 0.5 }
                  }}
                />
              )}
              {hasta && (
                <Chip
                  label={`Hasta: ${formatDateDisplay(hasta)}`}
                  size="small"
                  onDelete={() => setHasta('')}
                  icon={<CalendarIcon sx={{ fontSize: 16 }} />}
                  color="primary"
                  variant="outlined"
                  sx={{ 
                    height: 26,
                    '& .MuiChip-label': { fontSize: '0.7rem', px: 0.75 },
                    '& .MuiChip-icon': { fontSize: 16, ml: 0.5 }
                  }}
                />
              )}
            </Stack>
          )}
        </Box>

        <Divider sx={{ mb: 1.5 }} />

        {/* Campos de filtro */}
        <Grid container spacing={1.5} alignItems="flex-end">
          {/* Sucursal */}
          <Grid item xs={12} md={3.5}>
            <Typography variant="caption" color="textSecondary" sx={{ mb: 0.25, display: 'block', fontWeight: 500, fontSize: '0.65rem' }}>
              SUCURSAL
            </Typography>
            <SucursalAutocomplete
              value={sucursal}
              onChange={handleSucursalChange}
              disabled={loading}
              size="small"
            />
          </Grid>

          {/* Fecha Desde */}
          <Grid item xs={12} sm={6} md={2.5}>
            <Typography variant="caption" color="textSecondary" sx={{ mb: 0.25, display: 'block', fontWeight: 500, fontSize: '0.65rem' }}>
              FECHA DESDE
            </Typography>
            <TextField
              type="date"
              value={desde}
              onChange={handleDesdeChange}
              variant="outlined"
              fullWidth
              size="small"
              disabled={loading}
              InputLabelProps={{
                shrink: true,
              }}
              inputProps={{
                max: hasta || undefined,
              }}
              sx={{
                '& .MuiInputBase-input': { fontSize: '0.8rem', py: 0.75 }
              }}
            />
          </Grid>

          {/* Fecha Hasta */}
          <Grid item xs={12} sm={6} md={2.5}>
            <Typography variant="caption" color="textSecondary" sx={{ mb: 0.25, display: 'block', fontWeight: 500, fontSize: '0.65rem' }}>
              FECHA HASTA
            </Typography>
            <TextField
              type="date"
              value={hasta}
              onChange={handleHastaChange}
              variant="outlined"
              fullWidth
              size="small"
              disabled={loading}
              InputLabelProps={{
                shrink: true,
              }}
              inputProps={{
                min: desde || undefined,
              }}
              sx={{
                '& .MuiInputBase-input': { fontSize: '0.8rem', py: 0.75 }
              }}
            />
          </Grid>

          {/* Botones */}
          <Grid item xs={12} md={3.5}>
            <Stack direction="row" spacing={1}>
              <Button
                variant="contained"
                onClick={handleFilter}
                disabled={!isFormValid || loading}
                startIcon={<SearchIcon />}
                size="medium"
                fullWidth
                sx={{
                  height: '40px',
                  fontSize: '0.8rem',
                  fontWeight: 600,
                  textTransform: 'none',
                  boxShadow: isFormValid ? theme.shadows[1] : 'none',
                  '&:hover': {
                    boxShadow: theme.shadows[2],
                  }
                }}
              >
                {loading ? 'Buscando...' : 'Filtrar'}
              </Button>
              
              <Tooltip title="Limpiar todos los filtros">
                <IconButton 
                  onClick={handleClearFilters}
                  disabled={loading}
                  color="error"
                  size="medium"
                  sx={{ 
                    height: '40px', 
                    width: '40px',
                    border: `1px solid ${theme.palette.error.light}`,
                    '&:hover': {
                      backgroundColor: alpha(theme.palette.error.main, 0.04),
                    }
                  }}
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Stack>
          </Grid>
        </Grid>

        {/* Mensajes */}
        {(error || !isFormValid) && (
          <Box sx={{ mt: 1 }}>
            {error && (
              <Alert severity="error" sx={{ py: 0, '& .MuiAlert-message': { fontSize: '0.75rem' } }}>
                {error}
              </Alert>
            )}
            {!isFormValid && !error && (
              <Alert 
                severity="info" 
                sx={{ py: 0, '& .MuiAlert-message': { fontSize: '0.75rem' } }}
                icon={<FilterIcon sx={{ fontSize: 16 }} />}
              >
                {!sucursal && !desde && !hasta && 'Selecciona una sucursal y un rango de fechas.'}
                {!sucursal && (desde || hasta) && 'Selecciona una sucursal.'}
                {sucursal && (!desde || !hasta) && 'Selecciona un rango de fechas completo.'}
              </Alert>
            )}
          </Box>
        )}
      </Box>
    </Paper>
  );
};

export default FiltrosResumen;